import React, { useEffect, useMemo, useState } from "react";
import TableComponent from "../../components/TableComponent";
import {
  BuildingOffice2Icon,
  ChevronDownIcon,
  ChevronRightIcon,
  CurrencyDollarIcon,
  ExclamationCircleIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import PayMembershipFeeModal from "../../components/admin/PayMembershipfeeModal";
import { formatCurrencyVND } from "../../utils/helps";
import ApiCustom from "../../utils/ApiCustom";
import NotPaidMembershipfeeModal from "../../components/admin/NotPaidMembershipfeeModal";

const MembershipFees = () => {
  const [isPayMembershipFeeModalOpen, setIsPayMembershipFeeModalOpen] =
    useState(false);
  const [currentMembershipFee, setCurrentMembershipFee] = useState(null);
  const [isNotPaidModalOpen, setIsNotPaidModalOpen] = useState(false);
  const [notPaidFeeId, setNotPaidFeeId] = useState(null);
  const [membershipFees, setMembershipFees] = useState([]);
  const [selectedYear, setSelectedYear] = useState("all");
  const [openedYears, setOpenedYears] = useState({});

  const currentYear = new Date().getFullYear();

  const hasPermission = (requiredPermission) => {
    const userId = localStorage.getItem("userId");
    if (userId == 1) return true;
    const permissions = localStorage.getItem("permissions");
    const userPermissions = permissions
      ? JSON.parse(permissions)?.map((p) => p.permissionSlug)
      : [];
    return userPermissions.includes(requiredPermission);
  };

  const getYear = (item) => {
    if (item?.payStartDay) {
      const d = new Date(item.payStartDay);
      if (!isNaN(d.getTime())) return d.getFullYear();
    }
    const m = /\b(20\d{2}|19\d{2})\b/.exec(item?.title || "");
    return m ? Number(m[0]) : null;
  };

  const yearOptions = useMemo(() => {
    const set = new Set();
    membershipFees.forEach((it) => {
      const y = getYear(it);
      if (y) set.add(y);
    });
    return Array.from(set).sort((a, b) => b - a);
  }, [membershipFees]);

  const groupedByYear = useMemo(() => {
    return membershipFees.reduce((acc, item) => {
      const year = getYear(item) || "Không xác định";

      if (!acc[year]) {
        acc[year] = [];
      }

      acc[year].push(item);

      return acc;
    }, {});
  }, [membershipFees]);

  const filteredMembershipFees = useMemo(() => {
    if (selectedYear === "all") return membershipFees;
    return membershipFees.filter((it) => getYear(it) === Number(selectedYear));
  }, [membershipFees, selectedYear]);

  const oldYearOptions = useMemo(() => {
    return yearOptions.filter((year) => year !== currentYear);
  }, [yearOptions, currentYear]);

  const fetchMembershipFees = () => {
    ApiCustom("/membership-fee-shows")
      .then((res) => {
        if (res.status == 200) setMembershipFees(res.data?.data?.data || []);
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchMembershipFees();
  }, []);

  const handlePayMembershipFeeOpenModal = (membershipFee = null) => {
    setCurrentMembershipFee(membershipFee);
    setIsPayMembershipFeeModalOpen(true);
  };

  const handleClosePayMembershipFeeModal = () => {
    setIsPayMembershipFeeModalOpen(false);
    fetchMembershipFees();
  };

  const handleOpenNotPaidModal = (feeId) => {
    setNotPaidFeeId(feeId);
    setIsNotPaidModalOpen(true);
  };

  const handleCloseNotPaidModal = () => {
    setIsNotPaidModalOpen(false);
    setNotPaidFeeId(null);
  };

  const handleToggleYear = (year) => {
    setOpenedYears((prev) => ({
      ...prev,
      [year]: !prev[year],
    }));
  };

  const tableSettings = {
    renderSettings: (item) => (
      <div className="flex space-x-2">
        {hasPermission("MEMBERSHIP_FEE_UPDATE") && (
          <button
            title="Danh sách đóng hội phí"
            onClick={() => handlePayMembershipFeeOpenModal(item)}
            className="btn btn-outline btn-success btn-sm px-3 my-1 rounded"
          >
            <CurrencyDollarIcon className="w-5 h-5" />
          </button>
        )}
        {hasPermission("MEMBERSHIP_FEE_SHOWS") && (
          <button
            title="Hội viên chưa đóng hội phí"
            onClick={() => handleOpenNotPaidModal(item.id)}
            className="btn btn-outline btn-warning btn-sm px-3 my-1 rounded"
          >
            <ExclamationCircleIcon className="w-5 h-5" />
          </button>
        )}
      </div>
    ),
  };

  const headers = [
    { key: "title", label: "Chủ đề" },
    {
      key: "membershipFeeDisplay",
      label: "Mức hội phí",
      render: (item) => (
        <div className="flex flex-col gap-2 min-w-[260px]">
          <div className="flex items-center justify-between gap-3 rounded-lg bg-yellow-50 border border-yellow-100 px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="badge badge-warning badge-outline gap-1 py-3 px-2">
                <UserIcon className="w-4 h-4" />
                Cá nhân
              </span>
            </div>
            <span className="font-semibold text-yellow-700">
              {formatCurrencyVND(Number(item.individualFee || 0))}
            </span>
          </div>

          <div className="flex items-center justify-between gap-3 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="badge badge-info badge-outline gap-1 py-3 px-2">
                <BuildingOffice2Icon className="w-4 h-4" />
                Doanh nghiệp
              </span>
            </div>
            <span className="font-semibold text-blue-700">
              {formatCurrencyVND(Number(item.fee || 0))}
            </span>
          </div>
        </div>
      ),
    },
  ];

  if (
    hasPermission("MEMBERSHIP_FEE_UPDATE") ||
    hasPermission("MEMBERSHIP_FEE_SHOWS")
  ) {
    headers.push({ key: "setting", label: "Phương thức" });
  }

  const renderMembershipFeeTable = (data) => (
    <div className="rounded-lg shadow">
      <TableComponent headers={headers} data={data} settings={tableSettings} />
    </div>
  );

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Quản lý hội phí</h1>

      <div className="block md:flex justify-between items-center mb-4 gap-2">
        <div className="flex gap-2 items-center">
          <label className="text-sm font-medium">Lọc theo năm:</label>
          <select
            className="px-2 py-1 border rounded-lg focus:outline-none"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            <option value="all">Tất cả</option>
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div />
      </div>

      {selectedYear !== "all" ? (
        renderMembershipFeeTable(filteredMembershipFees)
      ) : (
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <span className="badge badge-success">Hiện tại</span>
                Hội phí năm {currentYear}
              </h2>
            </div>

            {renderMembershipFeeTable(groupedByYear[currentYear] || [])}
          </div>

          {oldYearOptions.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold mt-6">Hội phí năm cũ</h2>

              {oldYearOptions.map((year) => {
                const isOpen = openedYears[year];

                return (
                  <div
                    key={year}
                    className="border border-base-300 rounded-lg bg-base-100"
                  >
                    <button
                      type="button"
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-base-200 rounded-lg"
                      onClick={() => handleToggleYear(year)}
                    >
                      <div className="flex items-center gap-2">
                        {isOpen ? (
                          <ChevronDownIcon className="w-5 h-5" />
                        ) : (
                          <ChevronRightIcon className="w-5 h-5" />
                        )}
                        <span className="font-semibold">Hội phí năm {year}</span>
                        <span className="badge badge-ghost">
                          {(groupedByYear[year] || []).length} khoản
                        </span>
                      </div>

                      <span className="text-sm text-gray-500">
                        {isOpen ? "Thu gọn" : "Mở ra"}
                      </span>
                    </button>

                    {isOpen && (
                      <div className="p-4 pt-0">
                        {renderMembershipFeeTable(groupedByYear[year] || [])}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {isPayMembershipFeeModalOpen && (
        <PayMembershipFeeModal
          membershipFeeId={currentMembershipFee?.id || null}
          onClose={handleClosePayMembershipFeeModal}
        />
      )}

      {isNotPaidModalOpen && (
        <NotPaidMembershipfeeModal
          onClose={handleCloseNotPaidModal}
          membershipFeeId={notPaidFeeId}
        />
      )}
    </div>
  );
};

export default MembershipFees;